import get from 'lodash.get';

// Utils
// @ts-ignore
import createDeepEqualSelector from 'utils/reselect/createDeepEqualSelector';

// Selectors
// @ts-ignore
import { makeSelectPerson } from 'services/people/selectors';
// @ts-ignore
import { makeSelectPersonGroupIds } from 'services/groupPeople/selectors';
import { makeSelectEntityTypeFirstDepthValue } from 'services/entityTypes/selectors';

import orderBy from 'lodash.orderby';
import { EntityType } from 'services/entityTypes/types';
import { selectActiveEntitiesArray } from 'services/entities/selectors';
import { Entity, EntityWithComputedFields } from 'services/entities/types';
import { FormattedPerson, SectorNotifications } from 'services/people/types';

const selectSectorEntityTypeId = createDeepEqualSelector(
    [makeSelectEntityTypeFirstDepthValue('id')],
    (entityTypeState: EntityType): EntityType => entityTypeState
);

const selectFilteredSectors = createDeepEqualSelector(
    [selectActiveEntitiesArray, selectSectorEntityTypeId],
    (
        activeEntitiesArray: EntityWithComputedFields[],
        entityTypeIdState: number
    ) =>
        activeEntitiesArray.filter(
            (value) => entityTypeIdState === value.entityTypeId
        )
);

const selectOrderedSectors = createDeepEqualSelector(
    selectFilteredSectors,
    (filteredDataState: EntityWithComputedFields[]) =>
        orderBy(filteredDataState, 'name')
);

const makeSelectOrderedSectors = (): typeof selectOrderedSectors =>
    selectOrderedSectors;

const selectPersonSectorNotifications = createDeepEqualSelector(
    [makeSelectPerson(), makeSelectOrderedSectors()],
    (personState?: FormattedPerson, sectorsState?: Entity[]) =>
        sectorsState?.map((sector) => {
            const personSectorNotifications =
                personState?.sectorNotifications?.find(
                    (entity) => entity.entityId === sector.id
                );

            if (personSectorNotifications) {
                return {
                    entityId: personSectorNotifications.entityId,
                    peopleId: personSectorNotifications.peopleId,
                    isSubscribedToHarvestEmail:
                        personSectorNotifications.isSubscribedToHarvestEmail,
                    isSubscribedToHarvestSms:
                        personSectorNotifications.isSubscribedToHarvestSms
                } as SectorNotifications;
            }

            return {
                entityId: sector.id,
                peopleId: personState?.id ?? 0,
                isSubscribedToHarvestEmail: false,
                isSubscribedToHarvestSms: false
            } as SectorNotifications;
        })
);

const makeSelectPersonSectorNotifications =
    (): typeof selectPersonSectorNotifications =>
        selectPersonSectorNotifications;

const selectInitialValues = createDeepEqualSelector(
    [
        makeSelectPerson(),
        makeSelectPersonGroupIds(),
        makeSelectPersonSectorNotifications()
    ],
    (
        personState?: {},
        personGroupIdsState?: number[],
        sectorsState?: SectorNotifications[]
    ) => {
        let isCertificationPending = false;
        let isSprayOperator = false;

        const sprayCertificationId = get(personState, [
            'sprayCertification',
            'id'
        ]);
        const sprayCertificationEndDate = get(personState, [
            'sprayCertification',
            'endDate'
        ]);
        const sprayCertificationExternalId = get(personState, [
            'sprayCertification',
            'externalId'
        ]);

        if (sprayCertificationId && !sprayCertificationEndDate) {
            isSprayOperator = true;
            isCertificationPending = true;
        } else if (sprayCertificationId && sprayCertificationEndDate) {
            isSprayOperator = true;
        }

        return {
            ...personState,
            groupIds: personGroupIdsState,
            sprayCertification: {
                externalId: sprayCertificationExternalId,
                endDate: sprayCertificationEndDate
            },
            isSprayOperator,
            isCertificationPending,
            sectorNotifications: sectorsState
        };
    }
);

const makeSelectInitialValues = (): typeof selectInitialValues =>
    selectInitialValues;

export {
    selectInitialValues,
    makeSelectInitialValues,
    selectSectorEntityTypeId,
    makeSelectOrderedSectors
};
